/**
 * Export: materialise the current edit → encode → validate → hand the user a download. The gate
 * is hard: if validation finds any violation, or the encoded bytes don't decode cleanly, we do
 * NOT produce a file — we return the violations for the UI to show. `buildExport` is pure and
 * testable; `downloadExport` adds the browser save.
 */
import { decode } from '../codec/decode'
import { encode } from '../codec/encode'
import type { FitFile } from '../codec/types'
import type { EditState } from '../domain/edit'
import { materialize } from '../domain/materialize'
import { validate, type Violation } from '../domain/validate'
import type { Store } from './store'

export interface ExportResult {
  readonly violations: readonly Violation[]
  /** Present iff violations is empty. */
  readonly bytes?: Uint8Array
}

export function buildExport(original: FitFile, state: EditState): ExportResult {
  const output = materialize(state)
  const violations = validate(original, output)
  if (violations.length > 0) return { violations }

  const bytes = encode(output)
  try {
    decode(bytes) // final corruption check: our own decoder must accept what we wrote
  } catch (e) {
    return {
      violations: [{ check: 'encode-decode', detail: e instanceof Error ? e.message : String(e) }],
    }
  }
  return { violations: [], bytes }
}

/** `20867021941_ACTIVITY.fit` → `20867021941_ACTIVITY-edited.fit`. */
export function editedFileName(name: string): string {
  const base = name.replace(/\.fit$/i, '')
  return `${base}-edited.fit`
}

/**
 * Produce and download the edited file. Returns the violations (empty on success) so the caller
 * can surface a failure panel. No-ops if nothing is loaded.
 */
export function downloadExport(store: Store): readonly Violation[] {
  const original = store.originalFile.value
  const state = store.currentState.value
  const name = store.fileName.value
  if (!original || !state || name === null) return []

  const result = buildExport(original, state)
  if (!result.bytes) return result.violations

  // Copy into a plain ArrayBuffer so the Blob part is unambiguously typed.
  const buffer = result.bytes.buffer.slice(
    result.bytes.byteOffset,
    result.bytes.byteOffset + result.bytes.byteLength,
  ) as ArrayBuffer
  const blob = new Blob([buffer], { type: 'application/octet-stream' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = editedFileName(name)
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
  return []
}
